import { IDashBoardPageUI } from './types'
import { IconStatistics, IconUsers } from '@img/icons'

import css from './ui.module.scss'
import { Container, Empty, Flex } from '@ui/index'
import { ComponentsHeader } from '@primitive/sectionHeadings'

import { WidgetModskillAppChartContainer } from '@smart/widgets/modskill/applicationsChart'
import { WidgetYaskillAppChartContainer } from '@smart/widgets/yaskill/applicationsChart'
import { WidgetContractsChartContainer } from '@smart/widgets/contracts/contractsChart'

import imgEmty from '@img/empty.svg'
import imgAccessDenied from '@img/access_denied.svg'
import avatat from '@img/avatar_1.png'


const getActivityDate = (dateLastActivity: string | null) => {
    if (!dateLastActivity) return null

    const date = new Date(dateLastActivity)

    return Number.isNaN(date.getTime()) ? null : date
}

const formatInactiveTime = (dateLastActivity: string | null, activityNow: number, fallback: string) => {
    const activityDate = getActivityDate(dateLastActivity)
    if (!activityDate) return fallback || '—'

    const inactiveSeconds = Math.max(0, Math.floor((activityNow - activityDate.getTime()) / 1000))
    if (inactiveSeconds < 60) return `${inactiveSeconds} сек.`

    const inactiveMinutes = Math.floor(inactiveSeconds / 60)
    if (inactiveMinutes < 60) return `${inactiveMinutes} мин.`

    const inactiveHours = Math.floor(inactiveMinutes / 60)
    if (inactiveHours < 24) return `${inactiveHours} ч. ${inactiveMinutes % 60} мин.`

    return `${Math.floor(inactiveHours / 24)} д. ${inactiveHours % 24} ч.`
}

const formatLastActivityTime = (dateLastActivity: string | null) => {
    const activityDate = getActivityDate(dateLastActivity)

    return activityDate?.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }) || 'не определена'
}


/** UI - Страница мониторинга */
export const DashBoardPageUI = (props: IDashBoardPageUI) => {
    const isModskill = props.dashboardSource.value === 'modskill'

    return (
        <div className={css['container']}>
            <ComponentsHeader
                headerIcon={<IconStatistics width="20px" height="20px" />}
                headerTitle="Мониторинг"
                actions={<div className={css['source_switcher']}>
                    <button
                        type='button'
                        className={`${css['source_switcher_button']} ${isModskill ? css['source_switcher_button_active'] : ''}`}
                        onClick={() => props.dashboardSource.setValue('modskill')}
                    >
                        ModSkill
                    </button>

                    <button
                        type='button'
                        className={`${css['source_switcher_button']} ${!isModskill ? css['source_switcher_button_active'] : ''}`}
                        onClick={() => props.dashboardSource.setValue('yaskill')}
                    >
                        YaSkill
                    </button>
                </div>}
            />

            <div className={css['container_content']}>
                {props.isAccessDenied
                    ? <div className={css['access_denied_state']}>
                        <Empty image={imgAccessDenied} title='Доступ запрещён' />
                    </div>
                    : <>
                        <div className={css['widgets_content']}>
                            {isModskill
                                ? <WidgetModskillAppChartContainer getMonthName={props.getMonthName} />
                                : <WidgetYaskillAppChartContainer getMonthName={props.getMonthName} />
                            }
                        </div>

                        <div className={css['widgets_content']}>
                            <WidgetContractsChartContainer
                                key={isModskill ? 'contracts-chart-modskill' : 'contracts-chart-yaskill'}
                                getMonthName={props.getMonthName}
                                source={isModskill ? 'modskill' : 'yaskill'}
                                title={isModskill ? 'Договоры ModSkill' : 'Договоры YaSkill'}
                            />
                        </div>

                        {!props.isOperatorsAccessDenied &&
                            <Container className={css['operators_widget']}>
                                <div className={css['operators_widget_header']}>
                                    <Flex gap="8px" flexDirection="row" alignItems="center">
                                        <IconUsers width="24px" height="24px" />

                                        <div>
                                            <h4>Онлайн операторы</h4>
                                        </div>
                                    </Flex>

                                    <p className={css['operators_widget_badge']}>Онлайн: {props.onlineOperators.length}</p>
                                </div>

                                {props.isLoadingOnlineOperators && props.onlineOperators.length === 0 &&
                                    <div className={css['operators_empty_state']}>
                                        <p>Загрузка операторов...</p>
                                    </div>
                                }

                                {!props.isLoadingOnlineOperators && props.onlineOperators.length === 0 &&
                                    <div className={css['operators_empty_state']}>
                                        <Empty image={imgEmty} title='Сейчас нет операторов online' size='90px' />
                                    </div>
                                }

                                {props.onlineOperators.length > 0 &&
                                    <div className={css['operators_list']}>
                                        {props.onlineOperators.map((item) => {
                                            const firstInitial = item.firstName?.trim()?.charAt(0).toUpperCase() || ''
                                            const secondInitial = item.secondName?.trim()?.charAt(0).toUpperCase() || ''
                                            const initials = [firstInitial, secondInitial]
                                                .filter((value) => value)
                                                .map((value) => `${value}.`)
                                                .join('')
                                            const fullName = [item.lastName, item.firstName, item.secondName]
                                                .filter((value) => value && value.trim())
                                                .join(' ')
                                            const displayName = [item.lastName?.trim(), initials]
                                                .filter((value) => value)
                                                .join(' ')
                                            const lastPageName = item.lastPageName?.trim() || 'Последняя страница не определена'
                                            const inactiveTime = formatInactiveTime(item.dateLastActivity, props.activityNow, item.inactiveTime)
                                            const lastActivityTime = formatLastActivityTime(item.dateLastActivity)

                                            return (
                                            <div key={item.loginID} className={css['operators_item']}>
                                                <div className={css['operators_avatar']}>
                                                    <img src={avatat} alt="Оператор" />
                                                    <span className={css['operators_status_dot']}></span>
                                                </div>

                                                <p className={css['operators_name']} title={fullName}>{displayName || fullName}</p>
                                                <p className={css['operators_time']}>{inactiveTime}</p>
                                                <p className={css['operators_role']} title={item.accessLevelName}>{item.accessLevelName}</p>
                                                <p className={css['operators_last_page']} title={`Страница: ${lastPageName}`}>
                                                    <span className={css['operators_last_page_label']}>Страница:</span>
                                                    <span className={css['operators_last_page_value']}>{lastPageName}</span>
                                                </p>
                                                <p className={css['operators_last_activity']} title={`Последняя активность: ${lastActivityTime}`}>
                                                    Последняя активность: {lastActivityTime}
                                                </p>
                                            </div>
                                        )})}
                                    </div>
                                }
                            </Container>
                        }
                    </>
                }
            </div>
        </div>
    )
}